/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Agent List Component
 * Displays all AI agents with filtering and CRUD operations
 */

import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  SmartToy as AgentIcon,
  Build as ToolIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Editor from '@monaco-editor/react';

import { agentApi, llmModelApi } from '../../services/api';
import type { AgentListItem, AgentCreate, AgentUpdate, LlmModel } from '../../types';
import { PageHeader, LoadingSpinner, ConfirmDialog } from '../common';
import { AdminAgentToolbar } from './AdminAgentToolbar';
import { AgentSyncButton } from './AgentSyncButton';
import { useAuth } from '../../context/AuthContext';

export const AgentList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentListItem | null>(null);
  const [deletingAgent, setDeletingAgent] = useState<AgentListItem | null>(null);

  // Fetch agents
  const { data: agentsData, isLoading } = useQuery({
    queryKey: ['agents', searchTerm],
    queryFn: () => agentApi.list(1, 100),
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // Fetch LLM models for form
  const { data: modelsData } = useQuery({
    queryKey: ['models'],
    queryFn: () => llmModelApi.list({}),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => agentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toast.success('Agent deleted successfully');
      setDeletingAgent(null);
    },
    onError: (error: any) => {
      console.error('Failed to delete agent:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
      setDeletingAgent(null);
    },
  });

  const handleView = (agentId: number) => {
    navigate(`/ai/agents/${agentId}`);
  };

  const handleChat = (agentId: number) => {
    navigate(`/ai/agents/${agentId}/chat`);
  };

  const handleEdit = (agent: AgentListItem) => {
    setEditingAgent(agent);
    setFormOpen(true);
  };

  const handleDelete = (agent: AgentListItem) => {
    setDeletingAgent(agent);
  };

  const handleCreateNew = () => {
    setEditingAgent(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingAgent(null);
  };

  // ----- Import agent (from a previously exported JSON file) -----
  const importInputRef = useRef<HTMLInputElement | null>(null);

  const importMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return agentApi.importAgent(payload, { forceNew: true });
    },
    onSuccess: (imported) => {
      toast.success(`Imported agent "${imported.name}"`);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      if (imported?.id) {
        navigate(`/ai/agents/${imported.id}`);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message || 'Failed to import agent');
    },
  });

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      if (!payload || payload.export_type !== 'agent') {
        toast.error('File is not a valid agent export (export_type != "agent").');
        return;
      }
      importMutation.mutate(payload);
    } catch (err: any) {
      toast.error(`Could not read file: ${err?.message || err}`);
    }
  };

  // Filter agents based on search
  const filteredAgents = agentsData?.results.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Count agents that need sync (for admin toolbar)
  const pendingUpdatesCount = filteredAgents.filter(
    agent => agent.needs_sync === true
  ).length;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <PageHeader
        title="Agents"
        actions={
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Create Agent</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
            </Button>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={handleImportClick}
              disabled={importMutation.isPending}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Import</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Import</Box>
            </Button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={handleImportFileChange}
            />
            {user?.is_staff && (
              <AdminAgentToolbar pendingUpdatesCount={pendingUpdatesCount} />
            )}
          </Box>
        }
      />

      {/* Search */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <TextField
          fullWidth
          placeholder="Search agents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Agent List */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {filteredAgents.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                <AgentIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  No agents found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Create your first agent to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                  size="small"
                >
                  Create Agent
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          filteredAgents.map((agent) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={agent.id}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="div"
                      sx={{ 
                        fontSize: { xs: '0.95rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        pr: 1,
                      }}
                    >
                      {agent.name}
                    </Typography>
                    <Chip
                      label={agent.is_active ? 'Active' : 'Inactive'}
                      color={agent.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </Box>

                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      display: '-webkit-box',
                      WebkitLineClamp: { xs: 2, sm: 3 },
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {agent.description || 'No description provided'}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<ToolIcon />}
                      label={`${agent.tool_count} tools`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                    {agent.needs_sync && (
                      <Chip
                        label="Update"
                        color="warning"
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    )}
                    {agent.is_template && (
                      <Chip
                        label="Template"
                        color="warning"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                    Model: {agent.model_name}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 2 }, pt: 0 }}>
                  <Box>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleChat(agent.id)}
                      >
                        <ChatIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleView(agent.id)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                  </Box>
                  <Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(agent)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <AgentFormDialog
        open={formOpen}
        agent={editingAgent}
        models={modelsData?.results || []}
        onClose={handleFormClose}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['agents'] });
          handleFormClose();
        }}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deletingAgent}
        title="Delete Agent"
        message={`Are you sure you want to delete "${deletingAgent?.name}"? This action cannot be undone.`}
        onConfirm={() => deletingAgent && deleteMutation.mutate(deletingAgent.id)}
        onCancel={() => setDeletingAgent(null)}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

// Agent Form Dialog Component
interface AgentFormDialogProps {
  open: boolean;
  agent: AgentListItem | null;
  models: LlmModel[];
  onClose: () => void;
  onSuccess: () => void;
}

const AgentFormDialog: React.FC<AgentFormDialogProps> = ({
  open,
  agent,
  models,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [formData, setFormData] = useState<AgentCreate | AgentUpdate>({
    name: '',
    description: '',
    system_prompt: '',
    model_id: 0,
    agent_type: 'ASSISTANT',
    capabilities: [],
    is_active: true,
    is_template: false,
    template_source_id: undefined,
  });
  const [templates, setTemplates] = useState<AgentListItem[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const queryClient = useQueryClient();

  // Load templates on mount
  React.useEffect(() => {
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templateList = await agentApi.listTemplates();
        setTemplates(templateList);
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, []);

  // Initialize form when agent changes
  React.useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name,
        description: agent.description,
        system_prompt: '', // Will be loaded from detail
        model_id: 0, // Will be loaded from detail
        agent_type: 'ASSISTANT',
        is_active: agent.is_active,
        template_source_id: undefined,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        system_prompt: 'You are a helpful AI agent.',
        model_id: models[0]?.id || 0,
        agent_type: 'ASSISTANT',
        capabilities: [],
        is_active: true,
        template_source_id: undefined,
      });
    }
  }, [agent, models]);

  // Handle template selection
  const handleTemplateChange = (templateId: number | '') => {
    if (templateId === '') {
      setFormData({ 
        ...formData, 
        template_source_id: undefined,
        agent_type: 'ASSISTANT',
      });
    } else {
      const selectedTemplate = templates.find(t => t.id === templateId);
      setFormData({ 
        ...formData, 
        template_source_id: templateId,
        agent_type: selectedTemplate?.agent_type || 'ASSISTANT',
      });
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: AgentCreate) => agentApi.create(data),
    onSuccess: () => {
      toast.success('Agent created successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgentUpdate }) =>
      agentApi.update(id, data),
    onSuccess: () => {
      toast.success('Agent updated successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    },
  });

  const handleSubmit = () => {
    if (agent) {
      updateMutation.mutate({ id: agent.id, data: formData });
    } else {
      createMutation.mutate(formData as AgentCreate);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={window.innerWidth < 600}
    >
      <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        {agent ? 'Edit Agent' : 'Create New Agent'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, mt: { xs: 1, sm: 2 } }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
            size="small"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
            size="small"
          />

          {/* Template Selection Dropdown - Only show when creating (not editing) */}
          {!agent && (
            <FormControl fullWidth size="small">
              <InputLabel>Provision from Template (Optional)</InputLabel>
              <Select
                value={formData.template_source_id || ''}
                label="Provision from Template (Optional)"
                onChange={(e) => handleTemplateChange(e.target.value as number | '')}
                disabled={loadingTemplates}
              >
                <MenuItem value="">
                  <em>None - Create from scratch</em>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} ({template.agent_type === 'AI_SYSTEM_BUILDER' ? 'System Builder' : template.agent_type === 'MAIN_ASSISTANT' ? 'Main Agent' : template.agent_type === 'BOT_BUILDER' ? 'Bot Builder' : template.agent_type === 'MODEL_LAB_BUILDER' ? 'Model Lab Builder' : 'Agent'})
                    {template.owner_name ? (
                      <em style={{ marginLeft: 8, color: '#666' }}> — {template.owner_name}</em>
                    ) : null}
                  </MenuItem>
                ))}
              </Select>
              {formData.template_source_id && (
                <Typography variant="caption" color="info.main" sx={{ mt: 0.5 }}>
                  Agent will be provisioned from the selected template with pre-configured settings.
                </Typography>
              )}
            </FormControl>
          )}

          {/* Hide System Prompt when template is selected - it comes from template */}
          {!formData.template_source_id && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                System Prompt *
              </Typography>
              <Box 
                sx={{ 
                  border: 1, 
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  minHeight: { xs: '200px', sm: '300px' },
                  maxHeight: { xs: '300px', sm: '600px' },
                  resize: 'vertical',
                  '& .monaco-editor': {
                    minHeight: { xs: '200px !important', sm: '300px !important' },
                  }
                }}
              >
                <Editor
                  height={window.innerWidth < 600 ? "200px" : "300px"}
                  defaultLanguage="markdown"
                  value={formData.system_prompt}
                  onChange={(value) => setFormData({ ...formData, system_prompt: value || '' })}
                  theme={isDark ? 'vs-dark' : 'vs-light'}
                  options={{
                    minimap: { enabled: false },
                    lineNumbers: window.innerWidth < 600 ? 'off' : 'on',
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    fontSize: window.innerWidth < 600 ? 12 : 14,
                    padding: { top: 10, bottom: 10 },
                    automaticLayout: true,
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Define the agent's role and behavior
              </Typography>
            </Box>
          )}

          {/* Hide Agent Type when template is selected */}
          {!formData.template_source_id && (
            <FormControl fullWidth required size="small">
              <InputLabel>Agent Type</InputLabel>
              <Select
                value={formData.agent_type}
                label="Agent Type"
                onChange={(e) => setFormData({ ...formData, agent_type: e.target.value as 'ASSISTANT' | 'AI_SYSTEM_BUILDER' | 'MAIN_ASSISTANT' | 'BOT_BUILDER' | 'MODEL_LAB_BUILDER' })}
              >
                <MenuItem value="ASSISTANT">Agent</MenuItem>
                <MenuItem value="AI_SYSTEM_BUILDER">AI System Builder</MenuItem>
                <MenuItem value="MAIN_ASSISTANT">Main Agent</MenuItem>
                <MenuItem value="BOT_BUILDER">Bot Builder</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Hide LLM Model when template is selected - it comes from template */}
          {!formData.template_source_id && (
            <FormControl fullWidth required size="small">
              <InputLabel>LLM Model</InputLabel>
              <Select
                value={formData.model_id}
                label="LLM Model"
                onChange={(e) => setFormData({ ...formData, model_id: e.target.value as number })}
              >
                {models.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name} ({model.provider_name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
          />

          {/* Hide is_template toggle when template is selected */}
          {user?.is_staff && !formData.template_source_id && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_template || false}
                  onChange={(e) => setFormData({ ...formData, is_template: e.target.checked })}
                  color="warning"
                />
              }
              label="Template (Admin Only)"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createMutation.isPending || updateMutation.isPending}
          size="small"
        >
          {agent ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentList;
